import styled from '@emotion/styled'
import React, { useState } from 'react'
import type { OrganizationMember } from './OrganizationsSection'
import { Hugeicon } from '../../../../icons/Hugeicon'

type MemberCardProps = {
  membership: OrganizationMember
  isCurrentUser?: boolean
  isOwnerViewing?: boolean
  onRemove?: (membershipId: string) => void
}

const MemberCard: React.FC<MemberCardProps> = ({ membership, isCurrentUser, isOwnerViewing, onRemove }) => {
  const { role, roleTitle, pending, displayName, displayEmail } = membership
  const isOwner = role === 'owner'
  const [isHovered, setIsHovered] = useState(false)

  const canRemove = isOwnerViewing && !isOwner && onRemove

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onRemove) {
      onRemove(membership.id)
    }
  }

  return (
    <StyledCard
      isCurrentUser={isCurrentUser}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {canRemove && isHovered && (
        <StyledRemoveButton onClick={handleRemoveClick} title="Remove member">
          <CloseIcon />
        </StyledRemoveButton>
      )}
      <StyledAvatar isOwner={isOwner} isPending={pending}>
        {isOwner ? <CrownIcon /> : <UserIcon />}
      </StyledAvatar>
      <StyledNameRow>
        <StyledName>{displayName}</StyledName>
        {pending && <StyledInvitedBadge><ClockIcon /> INVITED</StyledInvitedBadge>}
      </StyledNameRow>
      {displayEmail && <StyledEmail>{displayEmail}</StyledEmail>}
      <StyledBadgeRow>
        {isOwner ? (
          <StyledOwnerBadge><CrownSmallIcon /> OWNER</StyledOwnerBadge>
        ) : roleTitle ? (
          <StyledRoleTitleBadge>{roleTitle.toUpperCase()}</StyledRoleTitleBadge>
        ) : null}
        {isCurrentUser && <StyledYouBadge>(You)</StyledYouBadge>}
      </StyledBadgeRow>
    </StyledCard>
  )
}

export default MemberCard

const CrownIcon: React.FC = () => <Hugeicon name="crown" size={24} />

const CrownSmallIcon: React.FC = () => <Hugeicon name="crown" size={12} />

const UserIcon: React.FC = () => <Hugeicon name="user" size={24} />

const CloseIcon: React.FC = () => <Hugeicon name="cancel" size={12} />

const ClockIcon: React.FC = () => <Hugeicon name="clock" size={10} />

const StyledCard = styled.div<{ isCurrentUser?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
  border: 2px solid ${props => (props.isCurrentUser ? '#f59e0b' : '#e5e7eb')};
  border-radius: 12px;
  background: ${props => (props.isCurrentUser ? '#fffbeb' : '#ffffff')};
  text-align: center;
  transition: border-color 0.15s ease-out;

  &:hover {
    border-color: ${props => (props.isCurrentUser ? '#f59e0b' : '#d1d5db')};
  }
`

const StyledRemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease-out;

  &:hover {
    background: #dc2626;
  }
`

const StyledAvatar = styled.div<{ isOwner: boolean; isPending?: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => {
    if (props.isOwner) return '#fef3c7'
    if (props.isPending) return '#fce7f3'
    return '#f3e8ff'
  }};
  color: ${props => {
    if (props.isOwner) return '#d97706'
    if (props.isPending) return '#db2777'
    return '#a855f7'
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`

const StyledNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`

const StyledName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #102638;
`

const StyledInvitedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
  background: #fce7f3;
  color: #db2777;
  border: 1px solid #f9a8d4;
`

const StyledEmail = styled.span`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 12px;
  word-break: break-all;
`

const StyledBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const StyledOwnerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  background: transparent;
  border: 1px solid #0d9488;
  color: #0d9488;
`

const StyledRoleTitleBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  background: transparent;
  border: 1px solid #a855f7;
  color: #a855f7;
`

const StyledYouBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #f59e0b;
`
